import { createElement, useState } from "react";
import { type ReactElement } from "react";
import { pdf, type DocumentProps } from "@react-pdf/renderer";
import InspectionReportPdf, {
  InspectionReportPdfProps,
} from "Components/Common/InspectionReportPdf/InspectionReportPdf";
import { DrawInspectionData } from "Pages/Admin/ProjectDetail/sections/DrawHistory/DrawHistory.types";

// react-pdf's <Image> fetches the src itself, and browsers block that read
// when the photo host (e.g. an S3 bucket) doesn't send CORS headers for the
// app's origin — even though a plain <img> tag displays the same URL fine.
// Pre-fetching as a blob and inlining it as a data URI sidesteps react-pdf's
// loader.
const fetchAsDataUri = async (url?: string): Promise<string | null> => {
  if (!url) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

export const useInspectionReportPdf = () => {
  const [isViewingReport, setIsViewingReport] = useState(false);

  const viewInspectionReportPdf = async (
    data: DrawInspectionData | null,
    drawNumber: number,
    projectName?: string,
  ) => {
    if (!data) return;
    setIsViewingReport(true);
    try {
      const sourcePhotos = data.photos ?? [];
      const resolvedPhotos = await Promise.all(
        sourcePhotos.map(async (photo) => ({
          ...photo,
          url: (await fetchAsDataUri(photo.url)) ?? undefined,
        })),
      );

      const props: InspectionReportPdfProps = {
        data: { ...data, photos: resolvedPhotos },
        drawNumber,
        projectName,
      };

      const el = createElement(
        InspectionReportPdf,
        props,
      ) as ReactElement<DocumentProps>;
      const blob = await pdf(el).toBlob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } finally {
      setIsViewingReport(false);
    }
  };

  return { viewInspectionReportPdf, isViewingReport };
};
